/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validateBirthday, useBirthdayValidationMessages} from '../../utils/birthday-utils'
import useSiteType from '../../hooks/use-site-type'
import {useXSSProtection} from './../../hooks/use-xss-protection'
import {useToast} from '../../hooks/use-toast'
import {LEGAL_AGE_EU, LEGAL_AGE_US} from '../../constants'

/**
 * Custom React hook that generates and returns a set of profile fields for a form.
 *
 * This hook is designed to be used with React Hook Form and provides a standardized
 * set of fields for capturing user profile information such as first name, last name,
 * email address, phone number, and date of birth. Each field is configured with
 * appropriate validation rules, labels, and default values.
 *
 * @param {Object} params - The parameters for configuring the profile fields.
 * @param {Object} params.form - The form object containing control and form state.
 * @param {string} [params.prefix=''] - An optional prefix to prepend to each field's name.
 */
export default function useProfileFields({
    form: {
        control,
        formState: {errors},
        setValue,
        clearErrors
    },
    prefix = ''
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const birthdayValidationMessages = useBirthdayValidationMessages()
    const {isUS} = useSiteType()
    const {validateField} = useXSSProtection()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage({
                defaultMessage: 'First Name',
                id: 'use_profile_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_profile_fields.error.required_first_name'
                }),
                validate: (value) => validateField(toast, value, 'profile-form', 'firstname')
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage({
                defaultMessage: 'Last Name',
                id: 'use_profile_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_profile_fields.error.required_last_name'
                }),
                validate: (value) => validateField(toast, value, 'profile-form', 'lastname')
            },
            error: errors[`${prefix}lastName`],
            control
        },
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email Address',
                id: 'use_profile_fields.label.email'
            }),
            placeholder: 'you@email.com',
            type: 'email',
            defaultValue: '',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_profile_fields.error.required_email'
                }),
                validate: (value) => validateField(toast, value, 'profile-form', 'email')
            },
            error: errors[`${prefix}email`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage({
                defaultMessage: 'Phone Number',
                id: 'use_profile_fields.label.phone'
            }),
            defaultValue: '',
            autoComplete: 'tel',
            error: errors[`${prefix}phone`],
            control,
            setValue, // pass setValue to update dial select on defaultCountry update
            clearErrors
        },
        birthday: {
            name: `${prefix}birthday`,
            label: formatMessage({
                defaultMessage: 'Date of Birth',
                id: 'use_profile_fields.label.birthday'
            }),
            type: 'date',
            defaultValue: '',
            autoComplete: 'date-of-birth',
            rules: {
                required: isUS ? birthdayValidationMessages.required : false,
                validate: {
                    isPastDate: (value) =>
                        validateBirthday(value).isPastDate || birthdayValidationMessages.invalid,
                    hasLegalAge: (value) => {
                        const ageLimit = isUS ? LEGAL_AGE_US : LEGAL_AGE_EU
                        return (
                            validateBirthday(value).hasLegalAge(ageLimit) ||
                            birthdayValidationMessages.hasLegalAge(ageLimit)
                        )
                    }
                }
            },
            error: errors[`${prefix}birthday`],
            control
        }
    }

    return fields
}
